import { pageTitle } from '@palaccount/config';
import type { CheckResponse, PaymentResponse } from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { Money } from '@palaccount/money';
import {
  CheckCircle2,
  Eraser,
  Hourglass,
  Info,
  Loader2,
  PiggyBank,
  Send,
  Trash2,
  Undo2,
  Wand2,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox, DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentToolbar } from '@/components/document-toolbar';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useCashAccountOptions, useExpenseAccountOptions } from '@/features/accounts/api';
import { FxPanel, useRateWarningGate } from '@/features/documents/fx-panel';
import { useInHandCheques } from '@/features/banking/api';
import { useCurrencyPickerCodes, useCurrencyTable } from '@/features/fx/api';
import { DocCurrencyCell, useDocCurrency } from '@/features/documents/doc-currency';
import { Typeahead, type TypeaheadOption } from '@/components/ui/typeahead';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { BILLS_KEY, PURCHASE_ORDERS_KEY, useBill } from '@/features/purchases/api';
import { useOrgSetting } from '@/features/admin/api';
import { needsApproval } from '@/features/purchases/po-rules';
import {
  autoAllocateAmount,
  autoAllocateOldestFirst,
  computeAllocationView,
} from '@/features/receipts/allocation';
import {
  blankTender,
  tendersFromResponse,
  tendersPayload,
  tendersVoucherTotal,
  validateTenders,
  type TenderRow,
} from '@/features/receipts/tenders';
import { TendersGrid } from '@/features/receipts/tenders-grid';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useDebounced, usePickerState } from '@/lib/typeahead';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  PAYMENTS_KEY,
  useAutoAllocatePayment,
  useAutoAllocatePaymentPreview,
  useCancelPayment,
  useCreatePayment,
  useDeletePayment,
  useOpenBills,
  useOpenBillSearch,
  usePartnerCredits,
  usePayment,
  useReversePayment,
  useSetPaymentAllocations,
  useSetPaymentApproval,
  useUpdatePayment,
} from './api';

type PaymentType = PaymentResponse['paymentType'];

const EXPENSE_CATEGORIES = [
  'rent',
  'utilities',
  'salaries',
  'office_supplies',
  'bank_fees',
  'other',
] as const;
const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;

interface GridRow {
  billId: string;
  billCode: string;
  billDate: string;
  billTotal: string;
  alreadyPaid: string;
  balanceDue: string;
}

/**
 * The credit grid's bill row: the PRINTED number and nothing else.
 *
 * No `hint`, deliberately. `<TypeaheadOption>`'s hint is "a code, a status, a
 * balance", and the obvious second line here — the balance due — is a figure
 * this screen states per bill in the grid above and tests against per bill in
 * the strip below. A third copy in a dropdown is one more place for it to be
 * stale by a page. Module scope so the identity is stable: `usePickerState`
 * memoises on it.
 */
const billOption = (bill: { id: string; code: string }): TypeaheadOption => ({
  id: bill.id,
  label: bill.code,
});

/**
 * FIN-04 — the Payment Voucher: the receipt voucher's mirror (same grid, same
 * two over-allocation tests, same oldest-first auto-allocate, same four-state
 * strip — §1: "everything about allocation works identically") plus the
 * APPROVAL panel that makes this screen different: Require approval (forced
 * on above 25,000 — Q-FIN-04-1), Pending/Approved/Rejected with the stamped
 * approver and date (BR-FIN-04-03/-04), the exact amber line under Post
 * (BR-FIN-04-02), self-approval refused server-side (Q-FIN-04-2), and every
 * control inert once locked (BR-FIN-04-07).
 *
 * Arriving with ?partnerId=&billId= (the purchasing entry points, PUR-05)
 * pre-selects the supplier and pre-allocates the bill — Q-FIN-04-6's
 * proposed default, adopted: retyping what the system already knows is where
 * allocation mistakes come from.
 */
export function PaymentEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { paymentId } = useParams<{ paymentId: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !paymentId;

  const { data: payment, isPending: loadingPayment } = usePayment(paymentId);
  const { options: cashOptions } = useCashAccountOptions();
  const { options: expenseOptions } = useExpenseAccountOptions();
  const createPayment = useCreatePayment();
  const updatePayment = useUpdatePayment(paymentId ?? '');
  const setAllocationsMutation = useSetPaymentAllocations(paymentId ?? '');
  const autoAllocateMutation = useAutoAllocatePayment(paymentId ?? '');
  /** The unsaved voucher's walk — the server's, over the whole open set. */
  const autoAllocatePreviewMutation = useAutoAllocatePaymentPreview();
  const setApprovalMutation = useSetPaymentApproval(paymentId ?? '');
  const deletePayment = useDeletePayment(paymentId ?? '');
  const cancelPayment = useCancelPayment(paymentId ?? '');
  const reversePayment = useReversePayment(paymentId ?? '');

  const canEdit = useCan(isNew ? 'accounting.create' : 'accounting.edit');
  const canApprove = useCan('accounting.approve');

  const [paymentDate, setPaymentDate] = useDocumentDate();
  const [paymentType, setPaymentType] = useState<PaymentType>('supplier');
  const [partnerId, setPartnerId] = useState(searchParams.get('partnerId') ?? '');
  /*
   * GEN-50 — FIN-04 §5's picker: the suppliers, searched on name or code,
   * with an empty result that says "No matching supplier" rather than showing
   * nothing. §5's "what we owe them" column is NOT on the row: `PartnerSchema`
   * carries `owedToUs` (the receivable) and no payable twin, and inventing one
   * in the browser from a page of bills is exactly the arithmetic this wave is
   * removing. The figure the voucher allocates against is on the screen below,
   * per bill, which is where it is checked.
   */
  const supplierPicker = usePartnerPicker(partnerId || null, { type: 'supplier' }, (partner) => ({
    id: partner.id,
    label: partner.name,
    hint: partner.code,
    inactive: !partner.isActive,
  }));

  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  const [debitAccountId, setDebitAccountId] = useState('');
  /**
   * S6 — THE TENDERS (PV-01 = RV-02…RV-06 read across to money out, plus the
   * endorsed cheque): one row per way the money went out. The default is one
   * cash row in the company's base currency, due today. BR-FIN-04-14's cheque
   * date is each cheque row's own due date now; the voucher's "amount" is
   * DERIVED from the rows below.
   */
  const base = useBaseCurrency();
  const [tenders, setTenders] = useState<TenderRow[]>(() => [blankTender(base, paymentDate)]);
  const [requireApproval, setRequireApproval] = useState(false);
  const [notes, setNotes] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  /**
   * PUR-04 §5 field 8's other half — the NOTE-FUNDED rows: per debit note,
   * which bill its standing credit is set against and how much. Kept apart
   * from `alloc` because these rows consume none of the voucher's cash
   * (the server excludes them from the amount budget and caps them per note
   * instead — payments.service validateAllocations).
   */
  const [creditAlloc, setCreditAlloc] = useState<
    Record<string, { billId: string; amount: string }>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** BR-FIN-04-11's ask-why dialog for the posted-voucher reversal. */
  const [reverseAsk, setReverseAsk] = useState(false);
  const [carriedBill, setCarriedBill] = useState(false);
  const [carriedCredit, setCarriedCredit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /**
   * BR-MD-11-14's question, in ADM-02 §8's shared box instead of the
   * `window.confirm` it used to ask through (BR-PERM-15, GEN-206's "never OK").
   * The state lives in the hook; this screen owns only the `await` at `onPost`
   * and the one render below.
   */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  /**
   * The void once asked with `window.confirm`, which BR-PERM-15 (spec line
   * 35168) forbids: "every irreversible action must use one of these two
   * patterns; a new pattern must not be invented", and browser chrome is
   * neither. The chrome went first (recorded then as "chrome removal only");
   * GEN-59's OTHER HALF IS NOW PAID TOO (spec line 3806: "Cancel must always
   * ask for a reason, and must STORE THAT REASON ON THE RECORD"): the
   * contract carries the reason (payments.ts cancel body: required, 1..500),
   * the service writes `cancel_reason` on the voucher and the reason onto
   * the audit row (20260903100000), and the cancelled banner below reads it
   * back. So this is a `ReasonBox` exactly like `reverseAsk` below — the
   * shape the two-button box's own comment said it should become.
   */
  const [cancelAsk, setCancelAsk] = useState(false);

  /**
   * GEN-209's FIFTH ACT — "approving or rejecting a submission" — spec line
   * 4709, verbatim: "The dialog must be used for: deleting a draft, cancelling
   * a confirmed document, switching a master record off, revoking an API key,
   * APPROVING OR REJECTING A SUBMISSION, posting a revaluation, and resetting
   * a layout." The three buttons below wrote straight to the server on click.
   *
   * ── WHY THIS HOLDS THE CANDIDATE AND NOT A BOOLEAN ────────────────────────
   *
   * One dialog serves both acts, so it has to know WHICH was asked. `null` is
   * "no question on screen"; the two members are the two acts GEN-209 names.
   * A boolean plus a separate "which" field would admit the state
   * `open && which === undefined`, which is a dialog with no verb in it.
   *
   * ── WHY `pending` IS NOT IN THIS TYPE, WHICH IS A DELIBERATE NARROWING ────
   *
   * The panel has THREE buttons and GEN-209 names TWO acts. Setting an
   * already-Approved voucher back to Pending withdraws an approval, and a good
   * case can be made that it is weightier than granting one — but GEN-209 does
   * not name it, and this build has already paid for citing a rule at an act
   * it does not name. So Pending still fires on click, and if it should ask,
   * that wants an owner ruling or a rule of its own rather than an inference
   * drawn from the shape of its two neighbours.
   *
   * ── WHY THIS IS A `ConfirmBox` AND NOT A `ReasonBox`, WHICH IS THE WHOLE
   *    JUDGEMENT AND MUST NOT BE READ OFF THE CANCEL ABOVE ───────────────────
   *
   * The cancel twenty lines up is a two-button box with its stored reason
   * still owed, because GEN-59 demands a reason for CANCEL and the contract
   * cannot yet carry one. NONE of that transfers here, and the symmetry is a
   * trap rather than an argument:
   *
   *   - GEN-59 (spec line 3806) is about Cancel and only Cancel: "Cancel must
   *     always ask for a reason, and must store that reason on the record."
   *     It says nothing about rejecting.
   *   - §16.5.3 (spec line 35165) ENUMERATES the acts that get the typed-reason
   *     pattern: "Leave rejection, lead disqualification, opportunity loss,
   *     candidate rejection, work-order substitution, and consumption
   *     corrections all use this second pattern." A payment-voucher decision
   *     is not among them, and every one that is lives in HR, CRM or
   *     manufacturing.
   *   - FIN-04's own rule block (spec lines 24850-24870) never mentions a
   *     reason. BR-FIN-04-03 governs what Rejected DOES — it clears the
   *     approval date — and stops there.
   *
   * And the evidence that settles it is a CONTRAST rather than an absence:
   * FIN-02's journal approval, one screen away, is specified WITH a reason —
   * "Rejection reason | Long text | Only when rejected" and "Reject … reveals
   * the reason box" (spec lines 24167, 24179). The specification knows how to
   * demand a rejection reason, and demanded one there and not here. That is a
   * decision, not an oversight, and it is why this is the two-button box.
   *
   * ── THE HALF THAT IS OPEN ANYWAY, SO NOBODY READS THE ABOVE AS A CEILING ──
   *
   * `SetApprovalRequestSchema` is `z.object({ status }).strict()`, and
   * `.strict()` REJECTS an unexpected field rather than ignoring it — so a
   * reason could not be sent today even if a rule appeared demanding one. Two
   * independent reasons for the same shape, and only one of them is a ruling.
   * `payment-approval-asks.test.tsx` asserts the schema's refusal at RUNTIME
   * rather than describing it here, so that widening the contract turns a test
   * red instead of leaving this paragraph quietly false.
   */
  const [approvalAsk, setApprovalAsk] = useState<'approved' | 'rejected' | null>(null);
  const loadedId = useRef<string | null>(null);

  const status = payment?.status ?? 'draft';
  const locked = status !== 'draft';
  const editable = !locked && canEdit;

  useEffect(() => {
    document.title = pageTitle(t('pay.editorTitle'));
  }, [t]);

  useEffect(() => {
    if (payment && loadedId.current !== `${payment.id}:${payment.createdAt}:${payment.status}`) {
      loadedId.current = `${payment.id}:${payment.createdAt}:${payment.status}`;
      setPaymentDate(payment.paymentDate);
      setPaymentType(payment.paymentType);
      setPartnerId(payment.partnerId ?? '');
      setExpenseCategory(payment.expenseCategory ?? '');
      setExpenseDescription(payment.expenseDescription ?? '');
      setDebitAccountId(payment.debitAccountId ?? '');
      // A legacy draft typed straight into the head has no rows yet; it is
      // offered one in its own currency so the amount can be re-stated.
      setTenders(
        (payment.tenders ?? []).length > 0
          ? tendersFromResponse(payment.tenders)
          : payment.status === 'draft'
            ? [blankTender(payment.currency ?? base, payment.paymentDate, { accountId: payment.paidFromAccountId })]
            : [],
      );
      setRequireApproval(payment.requireApproval);
      setNotes(payment.notes ?? '');
      // The two kinds of row part ways here: cash rows into the grid's own
      // cells, note-funded rows (purchaseReturnId set) into the credit rows.
      setAlloc(
        Object.fromEntries(
          payment.allocations
            .filter((row) => row.purchaseReturnId === null)
            .map((row) => [row.billId, row.amount]),
        ),
      );
      setCreditAlloc(
        Object.fromEntries(
          payment.allocations
            .filter((row) => row.purchaseReturnId !== null)
            .map((row) => [
              row.purchaseReturnId!,
              { billId: row.billId, amount: row.amount },
            ]),
        ),
      );
    }
  }, [payment]);

  const supplier = supplierPicker.byId.get(partnerId);
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  //
  // BR-FIN-04-15 — RETIRED by delegated ruling D49    rule-coverage:ignore
  // (27 Aug 2026, delivered by finfu--spec.patch), under D16's reading. Its
  // subject, the prototype's hard-coded six-code dropdown (the FIN-04 §5
  // note's codes: USD, EUR, GBP, AED, SAR, ILS), does not exist in the build.
  // The owner's per-document-currency decision (30 Oct 2026) put a currency
  // dropdown back on BOTH vouchers, but it is NOT that control: it offers the
  // tenant's own open currency list (`useCurrencyPickerCodes`, D16), never a
  // hard-coded six, and the receipt editor draws the IDENTICAL one — so the
  // "matching the receipt voucher" clause holds structurally while the "all
  // six" clause still names a control D16 ruled out. The ignore mark stays
  // for the claim: nothing here enforces the retired rule.
  //
  // The dropdown sits in front of the chain above; a pick clears the
  // allocation (the rows belonged to the other currency's bills — the server
  // clears them the same way).
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: payment?.currency,
    partnerCurrency: supplier?.currency ?? base,
    initialPick: isNew ? searchParams.get('currency') : null,
    onPick: (code) => {
      // A tender row not yet filled in follows the voucher into the new currency.
      setTenders((rows) => rows.map((row) => (row.amount.trim() === '' ? { ...row, currency: code } : row)));
      setAlloc({});
      setCreditAlloc({});
      setSeenBills(new Map());
      billPaging.resetPage();
    },
  });
  const currency = docCurrency.currency;
  /** Sent only for a supplier payment that picked one. */
  const currencyBody = () => (paymentType === 'supplier' ? docCurrency.body() : {});
  /** The open list and the preview are asked in the pick, else the saved voucher's; absent = the supplier's. */
  const settleCurrency = docCurrency.picked || payment?.currency || undefined;
  /** The rate table, for the grid's Rate defaults and the voucher-currency total. */
  const { data: currencyTable } = useCurrencyTable();
  const currencyCodes = useCurrencyPickerCodes();
  const rateFor = (code: string): string | null =>
    currencyTable?.rows.find((row) => row.code === code)?.rate ?? null;
  const voucherRate = currency === base ? '1' : rateFor(currency);
  /** The amount paid as the strip, the approval gate and Post read it — the rows' total. */
  const amount = useMemo(() => {
    const anyTyped = tenders.some((row) => MONEY_RE.test(row.amount.trim()));
    return anyTyped ? tendersVoucherTotal(tenders, currency, base, voucherRate) : '';
  }, [tenders, currency, base, voucherRate]);
  /** Auto-allocate's write-back and the carried bill land in the ONE row (see the receipt). */
  const setAmount = (next: string | ((current: string) => string)) => {
    const value = typeof next === 'function' ? next(amount) : next;
    setTenders((rows) => {
      if (rows.length > 1) return rows;
      const row = rows[0] ?? blankTender(currency, paymentDate);
      return [{ ...row, currency: row.amount.trim() === '' ? currency : row.currency, amount: value }];
    });
  };

  /**
   * PV-01's picker — the customer cheques still in hand. Picking one appends
   * an endorsed row that copies the register's facts read-only; only its rate
   * and Delete stay editable (the facts belong to the cheque).
   */
  const [chequeQuery, setChequeQuery] = useState('');
  const chequeSearch = useDebounced(chequeQuery);
  const { data: inHand, isPending: inHandLoading } = useInHandCheques(chequeSearch);
  const chequeOption = useCallback(
    (check: CheckResponse): TypeaheadOption => ({
      id: check.id,
      label: `${check.chequeNumber}${check.issuingBank ? ` · ${check.issuingBank}` : ''}`,
      hint: `${check.amount} ${check.currency} · ${check.chequeDate}${check.partnerName ? ` · ${check.partnerName}` : ''}`,
    }),
    [],
  );
  const chequePicker = usePickerState<CheckResponse>({
    query: chequeQuery,
    setQuery: setChequeQuery,
    search: chequeSearch,
    matches: inHand,
    held: undefined,
    toOption: chequeOption,
    loading: inHandLoading,
  });
  const onPickEndorsed = (id: string | null) => {
    if (id === null) return;
    const check = chequePicker.byId.get(id);
    if (!check || tenders.some((row) => row.chequeId === id)) return;
    setTenders((rows) => [
      // A blank default row gives way to the first real tender.
      ...rows.filter((row) => !(row.amount.trim() === '' && row.method === 'cash' && rows.length === 1)),
      blankTender(check.currency, paymentDate, {
        method: 'endorsed_check',
        amount: check.amount,
        rate: check.exchangeRate ?? (check.currency === base ? '1' : (rateFor(check.currency) ?? '')),
        chequeNo: check.chequeNumber,
        bankName: check.issuingBank ?? '',
        dueDate: check.chequeDate,
        chequeId: check.id,
        chequeCode: check.code,
      }),
    ]);
    setChequeQuery('');
  };

  /** The bill grid's own page (§3 step 12) — `?billPage=`. */
  const billPaging = usePagedState(DEFAULT_PAGE_SIZE, 'bill');

  /**
   * FIN-04 is FIN-03's mirror, one page at a time. `billsLoading` is the
   * FIRST load only (`keepPreviousData`), which is what the strip below
   * withholds its conclusion on: before the list has answered there is no
   * allocation to conclude about, but turning a page must not put the grid
   * back into that state.
   */
  const { data: billPage, isLoading: billsLoading } = useOpenBills(
    partnerId || null,
    billPaging.page,
    billPaging.pageSize,
    settleCurrency,
  );
  const openBills = billPage?.bills;
  const billRange = usePageRange(billPage, billPaging.pageSize);

  /**
   * Q-FIN-04-6: the purchasing entry point carries the bill across —
   * pre-allocate its full balance and fill the amount box.
   *
   * IT ASKS FOR THE BILL BY ID, and that is PAGINATION.md §3 step 8's answer
   * rather than a preference. The carry used to `find` its bill in the
   * open-bill LIST, which was correct only while that list was the whole open
   * set: with a page in front of it, arriving from the fifty-first oldest
   * open bill would find nothing and drop the carry silently — a paged read
   * truncating a resolve-a-record-by-id caller, which is exactly the defect
   * step 8 names. `useBill` answers about the ONE bill named, at any depth,
   * and its `balanceDue` is the same figure `open-bills` computes (grand
   * total less posted, unreversed allocations).
   *
   * The open-list membership the `find` used to imply is now explicit:
   * posted, and not fully paid.
   *
   * IT READS A PURCHASING ROUTE FROM AN ACCOUNTING SCREEN, and that is worth
   * saying out loud: `purchases/bills/:billId` carries `purchasing.view`,
   * where `open-bills` carries `accounting.view`. Nobody loses anything they
   * had — the ONLY way to arrive with `?billId=` is the Record payment link
   * on a bill, which is behind that same permission — and a hand-typed
   * `?billId=` that is refused simply does not arm the carry, which is the
   * safe direction. The alternative was an `ids` parameter on `open-bills`,
   * which is a wider change for a narrower gain.
   */
  const carriedBillId = isNew ? searchParams.get('billId') : null;
  const { data: carriedBillRow } = useBill(carriedBillId ?? undefined);

  /**
   * EVERY BILL THIS SCREEN HAS SEEN — balance due and code, accumulated
   * across pages rather than rebuilt from the page on screen.
   *
   * Two things need it, and both are §3 step 10/13 questions about the WHOLE
   * set rather than about the page:
   *  - the strip's per-bill over-allocation test (BR-FIN-04-06), which has to
   *    keep a bill red while the reader is on another page; and
   *  - the credit grid's "which bill does this note pay?" picker, which has
   *    to keep DISPLAYING the bill a saved voucher is already holding, at any
   *    depth (§3 step 8).
   *
   * IT SAYS "SCREEN" WHERE IT USED TO SAY "GRID", and the widening is the
   * GEN-50 conversion: the picker is a typeahead over the SERVER's open set
   * now, so a reader can name a bill the grid has never drawn. Its matches
   * are remembered here like any other row this screen has been told about,
   * which is what keeps two promises at once — the control still displays
   * the choice after the search box is cleared (`<Typeahead>`'s `selected`,
   * which resolves from here), and the combined per-bill cap below has the
   * balance it needs to paint a newly-reachable bill red.
   */
  const [seenBills, setSeenBills] = useState<
    ReadonlyMap<string, { code: string; balanceDue: string }>
  >(() => new Map());
  /**
   * Fold rows in, and hand back the SAME map when nothing is new — which is
   * what stops an effect that feeds this from re-running on its own output.
   */
  const rememberBills = useCallback(
    (seen: readonly { billId: string; code: string; balanceDue: string }[]) => {
      if (seen.length === 0) return;
      setSeenBills((current) => {
        let next: Map<string, { code: string; balanceDue: string }> | null = null;
        for (const row of seen) {
          const held = current.get(row.billId);
          if (held?.code === row.code && held.balanceDue === row.balanceDue) continue;
          next ??= new Map(current);
          next.set(row.billId, { code: row.code, balanceDue: row.balanceDue });
        }
        return next ?? current;
      });
    },
    [],
  );
  useEffect(() => {
    rememberBills([
      ...(openBills ?? []).map((bill) => ({
        billId: bill.billId,
        code: bill.billCode,
        balanceDue: bill.balanceDue,
      })),
      ...(payment?.allocations ?? []).map((row) => ({
        billId: row.billId,
        code: row.billCode,
        balanceDue: row.balanceDue,
      })),
      // Q-FIN-04-6's carried bill, which arrives by id and may live on any
      // page — see the carry effect below.
      ...(carriedBillRow
        ? [
            {
              billId: carriedBillRow.id,
              code: carriedBillRow.code,
              balanceDue: carriedBillRow.balanceDue,
            },
          ]
        : []),
    ]);
  }, [openBills, payment, carriedBillRow, rememberBills]);

  /** The whole-set balance source `computeAllocationView` takes. */
  const balances = useMemo(
    () => new Map([...seenBills].map(([billId, bill]) => [billId, bill.balanceDue])),
    [seenBills],
  );

  /** The supplier's POSTED debit notes — the standing credit (PUR-04 §5 f8). */
  const { data: partnerCredits } = usePartnerCredits(
    paymentType === 'supplier' ? partnerId || null : null,
  );

  /** Q-FIN-04-6's carry, on the bill the id above resolved. */
  useEffect(() => {
    if (!isNew || carriedBillId === null || carriedBill || !carriedBillRow) return;
    if (carriedBillRow.status !== 'posted' || carriedBillRow.fullyPaid) return;
    setCarriedBill(true);
    setAlloc({ [carriedBillRow.id]: carriedBillRow.balanceDue });
    setAmount((current) => (current.trim() === '' ? carriedBillRow.balanceDue : current));
  }, [isNew, carriedBillId, carriedBill, carriedBillRow]);

  /**
   * PUR-04 §5 field 8 / §13 step 6: "Apply to supplier credit … opens the
   * payment voucher screen, where the credit is set against what is owed."
   * Arriving with ?creditNoteId= pre-arms the note's credit against the
   * oldest open bill, at the smaller of the credit and that bill's balance —
   * the Q-FIN-04-6 carry, read across to the credit axis.
   *
   * "The OLDEST open bill" is the first row of the FIRST page, so the page is
   * part of the condition now rather than an assumption the old whole-list
   * read let it get away with: on any other page `openBills[0]` is that
   * page's first row, which is a different bill. Every link the product
   * builds carries no page, so this fires as it always did; a hand-built
   * `?billPage=2` link declines to arm rather than arming the wrong bill.
   */
  useEffect(() => {
    const creditNoteId = searchParams.get('creditNoteId');
    if (!isNew || creditNoteId === null || carriedCredit || !openBills || !partnerCredits) return;
    if (billPaging.page !== 1) return;
    const note = partnerCredits.find((candidate) => candidate.id === creditNoteId);
    const bill = openBills[0];
    if (!note || !bill) return;
    setCarriedCredit(true);
    const credit = Money.of(note.grandTotal, note.currency);
    const balance = Money.of(bill.balanceDue, note.currency);
    setCreditAlloc({
      [note.id]: {
        billId: bill.billId,
        amount: (credit.lessThan(balance) ? credit : balance).toFixed2(),
      },
    });
  }, [isNew, carriedCredit, openBills, partnerCredits, searchParams, billPaging.page]);

  /**
   * The grid: this PAGE of the supplier's open bills, oldest first, plus the
   * rows that belong to the voucher rather than to the open set.
   *
   * Two kinds join the page, and each appears exactly once:
   *  - the already-allocated STRAGGLERS a loaded voucher names that the open
   *    list no longer offers — appended on the LAST page, the one place that
   *    shows each of them once and leaves them reachable; and
   *  - Q-FIN-04-6's CARRIED bill, which arrives by id and can live on any
   *    page — drawn on the page the reader lands on, page one, when that page
   *    does not already hold it. A reader who came from a bill's Record
   *    payment must see that bill.
   *
   * None of this decides what is SUBMITTED: `allocationBody()` builds from
   * `alloc` and `creditAlloc` (§3 step 13).
   */
  const rows: GridRow[] = useMemo(() => {
    const open: GridRow[] = (openBills ?? []).map((bill) => ({ ...bill }));
    if (
      billPaging.page === 1 &&
      carriedBillRow &&
      !open.some((row) => row.billId === carriedBillRow.id)
    ) {
      open.unshift({
        billId: carriedBillRow.id,
        billCode: carriedBillRow.code,
        billDate: carriedBillRow.invoiceDate,
        billTotal: carriedBillRow.grandTotal,
        alreadyPaid: carriedBillRow.amountPaid,
        balanceDue: carriedBillRow.balanceDue,
      });
    }
    if (billPaging.page < billRange.pages) return open;
    for (const allocation of payment?.allocations ?? []) {
      if (!open.some((row) => row.billId === allocation.billId)) {
        open.push({
          billId: allocation.billId,
          billCode: allocation.billCode,
          billDate: allocation.billDate,
          billTotal: allocation.billTotal,
          alreadyPaid: allocation.alreadyPaid,
          balanceDue: allocation.balanceDue,
        });
      }
    }
    return open;
  }, [openBills, payment, carriedBillRow, billPaging.page, billRange.pages]);

  /**
   * The shared FIN-03 allocation mirror, read across to bills (FIN-04 §1) —
   * over the WHOLE allocation. Sourcing it from `rows` is PAGINATION.md §11's
   * first finding: behind a page it made `totalApplied` and BR-FIN-03-09's
   * unallocated figure describe fifty bills while the strip said "voucher".
   */
  const view = useMemo(
    () => computeAllocationView(amount, { amounts: alloc, balances }, currency),
    [amount, alloc, balances, currency],
  );

  /**
   * The credit grid's rows: the supplier's posted notes, plus any note a
   * LOADED voucher already names that the posted list no longer offers (a
   * `credited` note — the stragglers pattern the bill grid uses).
   */
  interface CreditRow {
    noteId: string;
    noteCode: string;
    noteDate: string | null;
    creditTotal: string | null;
  }
  const creditRows: CreditRow[] = useMemo(() => {
    const list: CreditRow[] = (partnerCredits ?? []).map((note) => ({
      noteId: note.id,
      noteCode: note.code,
      noteDate: note.returnDate,
      creditTotal: note.grandTotal,
    }));
    for (const allocation of payment?.allocations ?? []) {
      if (
        allocation.purchaseReturnId !== null &&
        !list.some((row) => row.noteId === allocation.purchaseReturnId)
      ) {
        list.push({
          noteId: allocation.purchaseReturnId,
          noteCode: allocation.purchaseReturnCode ?? '',
          noteDate: null,
          creditTotal: null,
        });
      }
    }
    return list;
  }, [partnerCredits, payment]);

  /**
   * GEN-50 — *"Every 'Pick from list' field must offer a search box inside its
   * dropdown once the list holds more than 10 records."*
   *
   * The credit grid's "which bill does this note pay?" field, converted. It
   * was a `<select>` over `seenBills`, which the paging wave described in its
   * own comment as "a `select` that stops lying about what it holds" — the
   * held choice was safe, and the OFFER was still whatever pages had been
   * turned. A supplier with 137 open bills offered fifty of them and said
   * nothing about the rest.
   *
   * ONE query state for the whole grid rather than one per row, and that is
   * forced rather than chosen: a hook cannot be called inside `creditRows
   * .map()`. It is also correct — only one combobox can be focused at a time,
   * and `<Typeahead>` clears the query on commit and on blur, so a row can
   * never open onto another row's search. The read is one panel of matches
   * (`PICKER_PAGE_SIZE`) against the SERVER's whole open set, debounced, and
   * it is a different query key from the grid's own page: typing here must
   * not move the grid.
   *
   * `held` is `seenBills` rather than a second bounded request — the
   * contract's note on the missing `ids` parameter, made good. Everything
   * this screen holds is already there: the voucher's own allocation rows
   * (which arrive carrying `billCode`), Q-FIN-04-6's carried bill via
   * `useBill`, every page the grid has drawn, and now every match the picker
   * has been shown. That is what makes `optionFor` answer for a bill on page
   * three after the search box has been cleared.
   */
  const [billQuery, setBillQuery] = useState('');
  const billSearch = useDebounced(billQuery);
  const { data: billMatches, isFetching: billSearching } = useOpenBillSearch(
    creditRows.length > 0 ? partnerId || null : null,
    billSearch,
  );
  useEffect(() => {
    rememberBills(
      (billMatches ?? []).map((bill) => ({
        billId: bill.billId,
        code: bill.billCode,
        balanceDue: bill.balanceDue,
      })),
    );
  }, [billMatches, rememberBills]);
  const billMatchRows = useMemo(
    () => (billMatches ?? []).map((bill) => ({ id: bill.billId, code: bill.billCode })),
    [billMatches],
  );
  /**
   * Ordered by the printed number rather than by which page happened to load
   * first — this feeds `optionFor` only, so the order is about nothing a
   * reader sees, but a stable one keeps the memo from churning.
   */
  const heldBillRows = useMemo(
    () =>
      [...seenBills]
        .map(([billId, bill]) => ({ id: billId, code: bill.code }))
        .sort((left, right) => left.code.localeCompare(right.code)),
    [seenBills],
  );
  const billPicker = usePickerState<{ id: string; code: string }>({
    query: billQuery,
    setQuery: setBillQuery,
    search: billSearch,
    matches: billMatchRows,
    held: heldBillRows,
    toOption: billOption,
    loading: billSearching,
  });

  /**
   * BR-FIN-04-06's shape with the credit axis: per BILL, cash and credit
   * together must not exceed the balance due. The cash-only mirror above
   * cannot see the credit cells, so the combined per-bill test runs here and
   * both grids paint the offending rows. (The strip's budget stays CASH
   * only — a credit row spends the note, not the voucher's amount, which is
   * exactly the server's arithmetic.)
   */
  const combinedOverRows = useMemo(() => {
    const over = new Set<string>(view.overRows);
    const perBill = new Map<string, Money>();
    const add = (billId: string, value: string) => {
      const trimmed = value.trim();
      if (!MONEY_RE.test(trimmed)) return;
      perBill.set(
        billId,
        (perBill.get(billId) ?? Money.zero(currency)).plus(Money.of(trimmed, currency)),
      );
    };
    // Over the CELLS, not over the rows on screen: a bill overdrawn on page 1
    // has to stay red — and keep Post refused — while the reader is on page 2.
    for (const [billId, cell] of Object.entries(alloc)) add(billId, cell);
    for (const credit of Object.values(creditAlloc)) add(credit.billId, credit.amount);
    for (const [billId, total] of perBill) {
      const balance = balances.get(billId);
      // A bill the grid has never drawn cannot have been typed into, and the
      // server's own walk caps every row at its balance — so there is nothing
      // to test rather than a figure to guess at. Same reading as
      // `computeAllocationView`'s per-row test; see `AllocationSet`.
      if (balance === undefined) continue;
      if (total.minus(Money.of(balance, currency)).greaterThan(Money.of('0.005', currency))) {
        over.add(billId);
      }
    }
    return over;
  }, [view.overRows, alloc, creditAlloc, balances, currency]);

  const paidFromCode =
    [
      ...new Set(
        tenders
          .filter((row) => row.method !== 'endorsed_check')
          .map((row) => cashOptions.find((option) => option.id === row.accountId)?.code)
          .filter((code): code is string => code !== undefined),
      ),
    ].join(' / ') || payment?.paidFromAccountCode;
  /**
   * BR-FIN-04-10 MEETS BR-FIN-04-14, and this line is where the two touch.
   *
   * The preview names the account posting will CREDIT. For a cheque dated
   * strictly later than the voucher, that account is not the one the money
   * will eventually come out of — the voucher credits 2116 Cheques Payable and
   * the bank leg is raised on the cheque date by the D31 sweep. Leaving the
   * paid-from code here would make the sentence above the Post button a claim
   * the posting contradicts, which is the exact defect this rule names and the
   * exact defect `fin04-preview-parity.test.tsx` exists to catch.
   *
   * The sentence itself does not change — `pay.postingPreview` interpolates
   * whatever account is credited, and it stays true in both languages because
   * what is substituted is a CODE, as it already was.
   *
   * The strictness is the product's, restated in the browser rather than
   * imported: this is a rendering, and a rendering is an input and not an
   * authority (GEN-211). The service asks `postingIsHeld` for itself and
   * refuses what it cannot hold; if the two ever disagreed, the server's
   * answer is the one in the ledger.
   *
   * ── THE CASE THIS PREVIEW CANNOT COVER, STATED RATHER THAN LEFT ─────────
   *
   * A post-dated cheque the REGISTER cannot carry — an Other-expense voucher
   * with nobody to make the cheque out to, or a paid-from account with no bank
   * record behind it, or petty cash — is refused at Post rather than posted.
   * The preview below still describes the entry, so for those three the
   * promised entry is never written.
   *
   * That is not fixable here and pretending otherwise would be worse. Whether
   * a bank record exists for a ledger account is not in anything this screen
   * fetches, so the browser can rule out ONE of the three and would then be
   * silently wrong about the other two — a preview that is right two thirds of
   * the time reads exactly like one that is right. The refusal names the
   * missing piece in its own sentence, immediately, which is a better place
   * for that answer than a guess above the button.
   */
  const previewChequeHeld = tenders.some(
    (row) => row.method === 'check' && row.dueDate !== '' && row.dueDate > paymentDate,
  );
  const previewEndorsed = tenders.some((row) => row.method === 'endorsed_check');
  const accountCode = [
    ...(previewChequeHeld ? ['2116'] : []),
    ...(previewEndorsed ? ['1114'] : []),
    ...(tenders.some((row) => row.method !== 'endorsed_check' && !(row.method === 'check' && row.dueDate > paymentDate)) && paidFromCode
      ? [paidFromCode]
      : []),
  ].join(' / ');
  const debitOption = expenseOptions.find((option) => option.id === debitAccountId);

  /**
   * BR-FIN-04-10 — "The journal preview must always match what posting
   * actually writes." The sentence above the Post button is a CLAIM about the
   * entry, so the two shapes where the two-account sentence would be false
   * get their own words:
   *
   *  - A PURE credit application (amount 0, note-funded rows) posts NO entry
   *    at all — `PaymentsService.post` writes one only `if (amount
   *    .isPositive())`, because the debit note's own §12 entry moved 2111
   *    when it posted and a pair of 0.00 legs would be a lie about money that
   *    never moved. Promising "this generates the journal entry: Debit
   *    2111 …" there was the preview's own lie, mirrored.
   *
   *  - A FOREIGN supplier settlement writes GEN-113's third pair — the
   *    realised difference to 4310/5310 at the frozen rate — which the
   *    two-account sentence never named. The added line says so; its "any"
   *    keeps it true when the bill's rate and the payment's happen to agree
   *    (`exchangeDifferenceLines` skips a zero gain).
   *
   * Mirrors the service's own tests: the entry-shape assertions cited to this
   * rule in `supplier-credit.e2e.spec.ts` and `fx-settlement.e2e.spec.ts`.
   */
  const noteFunded = Object.values(creditAlloc).some((row) => row.amount.trim() !== '');
  // Number(), not Money.of(): this runs on every keystroke and half-typed
  // input must not throw. NaN compares false, and the save path re-validates.
  const amountEntered = Number(amount.trim()) > 0;
  const previewNoteOnly = !amountEntered && noteFunded;
  const previewFxLine =
    !previewNoteOnly &&
    paymentType === 'supplier' &&
    currency !== base &&
    Object.values(alloc).some((value) => value.trim() !== '');

  /** Changing the supplier clears the whole allocation (BR-FIN-03-03 read across). */
  const onPickSupplier = (id: string) => {
    setPartnerId(id);
    setAlloc({});
    setCreditAlloc({});
    // The bills seen belonged to the OLD supplier, and a different party's
    // open set is a different list — BR-DSH-09-19's reset, on the only filter
    // this grid has.
    setSeenBills(new Map());
    billPaging.resetPage();
  };

  const onPickType = (next: PaymentType) => {
    if (!editable || !isNew || next === paymentType) return;
    setPaymentType(next);
    setPartnerId('');
    setAlloc({});
    setCreditAlloc({});
    setSeenBills(new Map());
    billPaging.resetPage();
  };

  // Q-FIN-04-1: above the company's treasury.payment_approval_limit setting
  // the switch is not the user's to leave off.
  const paymentLimit = useOrgSetting<string>('treasury.payment_approval_limit');
  const forcedApproval = needsApproval(amount.trim() || '0', currency, paymentLimit);
  const effectiveRequire = forcedApproval || requireApproval;
  const approvalStatus = payment?.approvalStatus ?? 'pending';
  // FIN-04 §5a: satisfied when the switch is off OR the status is Approved.
  const approvalSatisfied = !effectiveRequire || approvalStatus === 'approved';

  type AcctPermission =
    | 'accounting.create'
    | 'accounting.edit'
    | 'accounting.delete'
    | 'accounting.approve';
  const handleFailure = (thrown: unknown, permission: AcctPermission) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw that same cause away one expression later. `localizeServerCause`
    // keeps it: a refusal that ARRIVED keeps the server's sentence, localized,
    // and a request that never came back gets this screen's own sentence
    // instead of the browser's `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'pay.errSaveFailed'));
  };

  /**
   * THE PAYLOAD, FROM `alloc` — EVERY PAGE — AND NEVER FROM `rows`.
   *
   * PAGINATION.md §3 step 13. `setAllocations` REPLACES the collection, so
   * mapping over the rows on screen behind a paged read would send one page
   * and DELETE the allocations on all the others: auto-allocate across 120
   * bills, stay on page 1, press Save, and seventy allocation rows are gone
   * and the voucher settles bills nobody chose — silently, from a control
   * that reported success. The credit rows below were never sourced from
   * `rows` and are unchanged.
   *
   * `fin03-04-paged-allocation-save.test.tsx` is red against the `rows`
   * version of this expression and green against this one.
   */
  const allocationBody = () => [
    ...Object.entries(alloc)
      .map(([billId, cell]) => ({ billId, amount: cell.trim() }))
      .filter((row) => MONEY_RE.test(row.amount) && row.amount !== '0' && row.amount !== '0.00'),
    // The note-funded rows (PUR-04 §5 f8): purchaseReturnId marks a row as
    // spending the note's credit rather than the voucher's cash.
    ...Object.entries(creditAlloc)
      .map(([noteId, credit]) => ({
        billId: credit.billId,
        amount: credit.amount.trim(),
        purchaseReturnId: noteId,
      }))
      .filter(
        (row) =>
          row.billId !== '' &&
          MONEY_RE.test(row.amount) &&
          row.amount !== '0' &&
          row.amount !== '0.00',
      ),
  ];

  const validate = (): string | null => {
    if (paymentType === 'supplier' && !partnerId) return t('pay.errSupplierRequired');
    if (paymentType === 'expense' && !debitAccountId) return t('pay.errDebitAccountRequired');
    const problem = validateTenders(tenders);
    if (problem === 'amountFormat') return t('pay.errAmountFormat');
    if (problem === 'accountRequired') return t('pay.errAccountRequired');
    if (problem === 'chequeNoRequired') return t('tenders.errChequeNoRequired');
    if (problem === 'dueDateRequired') return t('tenders.errDueDateRequired');
    if (problem === 'rateFormat') return t('tenders.errRateFormat');
    return null;
  };

  const save = async (navigateAfter = true): Promise<PaymentResponse | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    try {
      if (isNew) {
        const rows = tendersPayload(tenders, 'payment');
        const created = await createPayment.mutateAsync({
          paymentDate,
          paymentType,
          ...currencyBody(),
          ...(rows.length > 0
            ? { tenders: rows }
            : {
                method: tenders[0]?.method === 'endorsed_check' ? 'cash' : (tenders[0]?.method ?? 'cash'),
                paidFromAccountId: tenders[0]?.accountId ?? '',
              }),
          requireApproval: effectiveRequire,
          ...(paymentType === 'supplier'
            ? {
                partnerId,
                ...(allocationBody().length > 0 ? { allocations: allocationBody() } : {}),
              }
            : {
                debitAccountId,
                ...(expenseCategory.trim() ? { expenseCategory: expenseCategory.trim() } : {}),
                ...(expenseDescription.trim()
                  ? { expenseDescription: expenseDescription.trim() }
                  : {}),
              }),
          ...(notes.trim() ? { notes } : {}),
        });
        if (navigateAfter) void navigate(`/accounting/payments/${created.id}`, { replace: true });
        return created;
      }
      const partnerChanged = payment?.partnerId !== partnerId;
      const rows = tendersPayload(tenders, 'payment');
      const updated = await updatePayment.mutateAsync({
        paymentDate,
        ...(paymentType === 'supplier' && partnerChanged ? { partnerId } : {}),
        ...currencyBody(),
        ...(rows.length > 0
          ? { tenders: rows }
          : tenders[0]?.accountId
            ? { paidFromAccountId: tenders[0].accountId }
            : {}),
        requireApproval: effectiveRequire,
        ...(paymentType === 'expense'
          ? {
              debitAccountId,
              expenseCategory: expenseCategory.trim() === '' ? null : expenseCategory.trim(),
              expenseDescription:
                expenseDescription.trim() === '' ? null : expenseDescription.trim(),
            }
          : {}),
        notes: notes.trim() === '' ? null : notes,
      });
      if (paymentType === 'expense') {
        loadedId.current = null;
        return updated;
      }
      const reallocated = await setAllocationsMutation.mutateAsync({
        allocations: allocationBody(),
      });
      loadedId.current = null;
      return reallocated;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'accounting.create' : 'accounting.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  /**
   * Auto-allocate, oldest first (BR-FIN-03-10 read across). BOTH paths are the
   * server's walk now — the unsaved voucher asks the preview route, which
   * spreads over the supplier's WHOLE open set rather than over the rows the
   * grid is showing. The browser fold is what kept `GET /api/payments/open-bills`
   * unpaged; moving it here is what makes paging that list possible later.
   */
  const onAutoAllocate = async () => {
    setError(null);
    if (isNew) {
      // No supplier picked: nothing open to walk, and no party to ask about.
      if (!partnerId) {
        const empty = autoAllocateOldestFirst(amount, [], currency);
        setAlloc(empty.amounts);
        setAmount(empty.amountReceived);
        return;
      }
      setBusy(true);
      try {
        const amountToSpread = autoAllocateAmount(amount, currency);
        const preview = await autoAllocatePreviewMutation.mutateAsync({
          partnerId,
          ...(settleCurrency ? { currency: settleCurrency } : {}),
          ...(amountToSpread === undefined ? {} : { amount: amountToSpread }),
        });
        // The cash spread only — the note-funded rows in `creditAlloc` spend a
        // debit note's standing credit rather than this voucher's money, so the
        // wand never touches them (the saved path re-attaches them for the same
        // reason).
        setAlloc(Object.fromEntries(preview.allocations.map((row) => [row.billId, row.amount])));
        setAmount(preview.amountReceived);
      } catch (thrown) {
        // The read permission is already held by anyone on this screen, so a
        // write-permission denial would be the wrong message.
        setError(localizeServerCause(t, thrown, 'pay.errSaveFailed'));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      const saved = await save();
      if (!saved) return;
      const auto = await autoAllocateMutation.mutateAsync();
      // The server's auto-allocate recomputes the CASH spread and replaces
      // every row — re-attach the note-funded rows on top of its answer, so
      // pressing the wand never silently drops a credit (they spend the
      // note, not the budget the wand just spread).
      const credits = Object.entries(creditAlloc).filter(
        ([, credit]) =>
          credit.billId !== '' &&
          MONEY_RE.test(credit.amount.trim()) &&
          credit.amount.trim() !== '0' &&
          credit.amount.trim() !== '0.00',
      );
      if (credits.length > 0) {
        await setAllocationsMutation.mutateAsync({
          allocations: [
            ...auto.allocations
              .filter((row) => row.purchaseReturnId === null)
              .map((row) => ({ billId: row.billId, amount: row.amount })),
            ...credits.map(([noteId, credit]) => ({
              billId: credit.billId,
              amount: credit.amount.trim(),
              purchaseReturnId: noteId,
            })),
          ],
        });
      }
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'accounting.edit');
    } finally {
      setBusy(false);
    }
  };

  const onClear = () => {
    setAlloc({});
    setCreditAlloc({});
  };

  /** BR-FIN-04-03: the approval action — server-stamped, server-audited. */
  const onSetApproval = async (next: 'pending' | 'approved' | 'rejected') => {
    if (isNew || !paymentId) return;
    setBusy(true);
    setError(null);
    try {
      await setApprovalMutation.mutateAsync(next);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'accounting.approve');
    } finally {
      setBusy(false);
    }
  };

  const onPost = async () => {
    // BR-MD-11-14 at the confirming click, before the save that precedes it.
    if (!(await confirmRateWarning(payment?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await save(false);
      if (!saved) return;
      const response = await api.payments.post({ params: { paymentId: saved.id }, body: {} });
      if (response.status !== 200) {
        if (response.status === 403) denyPermission('accounting.approve');
        const body = response.body;
        setError(
          localizeServerMessage(
            t,
            typeof body === 'object' && body !== null && 'message' in body
              ? String((body as { message: unknown }).message)
              : t('common.loadFailed'),
          ),
        );
        if (isNew) void navigate(`/accounting/payments/${saved.id}`, { replace: true });
        return;
      }
      loadedId.current = null;
      // Posting moved bill balances and the order strips.
      void queryClient.invalidateQueries({ queryKey: PAYMENTS_KEY });
      void queryClient.invalidateQueries({ queryKey: BILLS_KEY });
      void queryClient.invalidateQueries({ queryKey: PURCHASE_ORDERS_KEY });
      if (isNew) void navigate(`/accounting/payments/${saved.id}`, { replace: true });
    } catch (thrown) {
      handleFailure(thrown, 'accounting.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!paymentId) return;
    setDeleting(false);
    setBusy(true);
    try {
      await deletePayment.mutateAsync();
      void navigate('/accounting/payments');
    } catch (thrown) {
      handleFailure(thrown, 'accounting.delete');
    } finally {
      setBusy(false);
    }
  };

  /** GEN-59: the ReasonBox hands over the typed reason; it rides the wire. */
  const onCancelDraft = async (reason: string) => {
    if (!paymentId) return;
    setCancelAsk(false);
    setBusy(true);
    try {
      await cancelPayment.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'accounting.edit');
    } finally {
      setBusy(false);
    }
  };

  /**
   * BR-FIN-04-11 / R8 — the posted-voucher correction. The ReasonBox IS the
   * confirm: the reason is required (GEN-72's shape, audited server-side),
   * so Escape, click-outside or an empty box abandons the act (ADM-02 §8:
   * no browser chrome).
   */
  const onReverse = async (reason: string) => {
    if (!paymentId) return;
    setReverseAsk(false);
    setBusy(true);
    setError(null);
    try {
      await reversePayment.mutateAsync(reason);
    } catch (thrown) {
      handleFailure(thrown, 'accounting.approve');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingPayment) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  const amountAboveZero = MONEY_RE.test(amount.trim()) && !/^0+(\.0+)?$/.test(amount.trim());
  const postable =
    amountAboveZero &&
    validateTenders(tenders) === 'none' &&
    approvalSatisfied &&
    (paymentType === 'supplier'
      ? Boolean(partnerId) && view.state !== 'over'
      : Boolean(debitAccountId));

  /**
   * FIN-04's mirror of the receipt strip, and the same withholding: before the
   * open-bill list lands there is no allocation, so the strip states no
   * conclusion about one. (See receipt-editor-page.tsx for the full reasoning.)
   */
  const stripState = billsLoading ? 'pending' : view.state;
  const strip = {
    pending: {
      icon: <Loader2 className="size-4 animate-spin" aria-hidden />,
      className: 'border-t-muted-foreground/40 bg-muted/40 text-muted-foreground',
      text: t('pay.stripPending'),
    },
    idle: {
      icon: <Info className="size-4" aria-hidden />,
      className: 'border-t-muted-foreground/40 bg-muted/40 text-muted-foreground',
      text: t('pay.stripIdle'),
    },
    over: {
      icon: <XCircle className="size-4" aria-hidden />,
      className: 'border-t-red-500 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
      text: t('pay.stripOver'),
    },
    credit: {
      icon: <PiggyBank className="size-4" aria-hidden />,
      className:
        'border-t-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
      text: t('pay.stripCredit', { amount: view.unallocated }),
    },
    full: {
      icon: <CheckCircle2 className="size-4" aria-hidden />,
      className:
        'border-t-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
      text: t('pay.stripFull'),
    },
  }[stripState];

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's box, rendered UNCONDITIONALLY — deliberately unlike the
          delete and cancel dialogs below, which sit inside the same conditional
          as their trigger so they cannot outlive it. This one is awaited: if
          its condition flipped while the question was on screen the dialog
          would unmount, the promise would never settle, and the post would hang
          with no error anywhere. There is no condition to tie it to in any
          case — the hook's own state is non-null only between the click and
          the answer. */}
      {rateWarningDialog}
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('pay.editorTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {payment ? <bdi dir="ltr">{payment.code}</bdi> : t('pay.codeNew')}
        </span>
        <DocStatusChip status={status} postedTone="green" />
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. The
            approval trio is NOT here and is not an omission: see the panel at
            §5a below, where the reason it is a different control class is
            written down. */}
        <DocumentToolbar
          actions={[
            {
              // Not PART 4.6's Cancel row — a link back to the register wearing
              // the same word. The act the table means is `pay.cancelDraft` two
              // rows below; the primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/accounting/payments',
            },
            {
              // The document's question and the role's, kept apart (16.7).
              // BR-FIN-04-07 locks every control once the voucher leaves draft,
              // and `locked` is that boundary — so a posted or cancelled
              // voucher has no Delete for anybody, the Owner included
              // (BR-PERM-02, GEN-70), while a reader on a DRAFT one still sees
              // it greyed with the reason.
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && !locked,
              gate: { permission: 'accounting.delete', reasonKey: 'perm.cannot.deletePayments' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              // PART 4.6's Cancel row proper — the act that ends the voucher's
              // validity, which the primitive's header names by this exact
              // spelling. GEN-59: it asks for a reason and stores it.
              id: 'cancelDraft',
              row: 'cancel',
              label: t('pay.cancelDraft'),
              variant: 'outline',
              visible: !isNew && !locked,
              gate: { permission: 'accounting.edit', reasonKey: 'perm.cannot.editPayments' },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
            {
              // BR-FIN-04-11 — the ONLY action a posted voucher offers. Gone
              // once reversed: one reversal ever (FIN-02 §15). `row: null` for
              // the receipt voucher's reason, read across: reversing undoes a
              // posted voucher's accounting, which is PART 4.6's sentence for
              // **Cancel**, but this screen already declares that row on
              // `pay.cancelDraft` and the table names buttons by the word
              // printed on them. "Reverse payment" is not one of its words.
              id: 'reverse',
              row: null,
              label: t('pay.reversePayment'),
              icon: <Undo2 aria-hidden />,
              variant: 'destructive',
              visible: status === 'posted' && !payment?.reversedAt,
              gate: { permission: 'accounting.approve', reasonKey: 'perm.cannot.reversePayments' },
              disabled: busy,
              onAct: () => setReverseAsk(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: !locked,
              gate: {
                permission: isNew ? 'accounting.create' : 'accounting.edit',
                reasonKey: isNew ? 'perm.cannot.createPayments' : 'perm.cannot.editPayments',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && !locked ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('pay.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* §8's typed-reason dialog (GEN-59). `pay.confirmCancel` was
              already a question so it is GEN-205's title unchanged; the
              body carries the consequence, the undone clause and the
              stored-reason sentence. Inside the same conditional as the
              button, so the dialog cannot outlive the state that allows
              the void — the convention the delete above set.
              `gen72.confirmCancel` is the verb — deliberately NOT the
              trigger's exact words (reason-box.tsx's own rule). */}
          {!isNew && !locked ? (
            <ReasonBox
              open={cancelAsk}
              title={t('pay.confirmCancel')}
              prompt={t('pay.confirmCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => void onCancelDraft(reason)}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
          {/* Unconditional, exactly as it already was — unlike the two above
              this box has no conditional to sit inside: `reverseAsk` can only
              be true between the click and the answer, and the click is
              already behind the button's own `visible`. */}
          <ReasonBox
            open={reverseAsk}
            title={t('pay.reversePayment')}
            prompt={t('pay.reversePrompt')}
            reasonLabel={t('reversal.reasonLabel')}
            confirmLabel={t('reversal.confirm')}
            busy={busy}
            onConfirm={(reason) => void onReverse(reason)}
            onCancel={() => setReverseAsk(false)}
          />
        </DocumentToolbar>
      </div>

      {/* The reversed notice outranks the posted one (FIN-02 §7's priority
          read across); both directions of the pair are on screen — this
          banner links the reversing entry, that entry links back. */}
      {payment?.reversedAt ? (
        <div className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          <p>
            {t('pay.reversedBanner', { date: payment.reversedAt.slice(0, 10) })}{' '}
            {payment.reversalJournalEntryId ? (
              <Link
                to={`/accounting/journals/${payment.reversalJournalEntryId}`}
                className="font-medium underline"
              >
                {t('pay.reversedByLink', { no: payment.reversalEntryCode ?? '' })}
              </Link>
            ) : null}
          </p>
          {payment.reversalReason ? (
            <p className="mt-1 opacity-90">
              {t('pay.reversedReason', { reason: payment.reversalReason })}
            </p>
          ) : null}
        </div>
      ) : status === 'posted' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('pay.lockedPosted')}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD. Rows cancelled
              before the reason existed keep the old sentence. */}
          {payment?.cancelReason != null
            ? t('pay.cancelledReasonNotice', { reason: payment.cancelReason })
            : t('pay.lockedCancelled')}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── The top panel (FIN-04 §5) ──────────────────────────────────── */}
      <Card>
        <CardContent className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label htmlFor="pv-date">{t('common.date')}</Label>
              <Input
                id="pv-date"
                type="date"
                dir="ltr"
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
                disabled={!editable}
              />
            </div>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium leading-none">{t('pay.fType')}</span>
              <div className="grid h-12 grid-cols-2 gap-1 rounded-[11px] bg-muted p-1">
                {(['supplier', 'expense'] as const).map((candidate) => (
                  <button
                    key={candidate}
                    type="button"
                    aria-pressed={paymentType === candidate}
                    onClick={() => onPickType(candidate)}
                    disabled={!editable || !isNew}
                    className={cn(
                      'rounded-md text-sm font-medium transition-colors',
                      paymentType === candidate
                        ? 'bg-card shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                      (!editable || !isNew) && paymentType !== candidate && 'opacity-60',
                    )}
                  >
                    {t(candidate === 'supplier' ? 'pay.typeSupplier' : 'pay.typeExpense')}
                  </button>
                ))}
              </div>
            </div>
            {paymentType === 'supplier' ? (
              <div className="grid gap-1.5">
                <Label htmlFor="pv-supplier">{t('po.fSupplier')}</Label>
                <PartnerTypeahead
                  entity="supplier"
                  id="pv-supplier"
                  label={t('po.fSupplier')}
                  value={partnerId || null}
                  onChange={(id) => onPickSupplier(id ?? '')}
                  options={supplierPicker.options}
                  selected={supplierPicker.optionFor(partnerId)}
                  query={supplierPicker.query}
                  onQueryChange={supplierPicker.setQuery}
                  placeholder={t('po.pickSupplier')}
                  emptyLabel={t('picker.noMatchingSupplier')}
                  loading={supplierPicker.loading}
                  disabled={!editable}
                />
              </div>
            ) : null}
            {/* Per-document currency (2026-10-30): the voucher settles only
                bills in its OWN currency; a pick clears the allocation. */}
            {paymentType === 'supplier' ? (
              <DocCurrencyCell
                idPrefix="pv"
                value={currency}
                onChange={docCurrency.pick}
                disabled={!editable}
              />
            ) : (
              <div className="grid gap-1.5">
                {/* BR-FIN-04-05 — only expense accounts are offered. */}
                <Label htmlFor="pv-debit-account">{t('pay.fDebitAccount')}</Label>
                <NativeSelect
                  id="pv-debit-account"
                  value={debitAccountId}
                  onChange={(event) => setDebitAccountId(event.target.value)}
                  disabled={!editable}
                >
                  <option value="">{t('receipts.pickAccount')}</option>
                  {expenseOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.code} — {option.name}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}
          </div>

          {paymentType === 'expense' ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="grid gap-1.5">
                {/* Six choices (FIN-04 §5 field 10). */}
                <Label htmlFor="pv-expense-category">{t('pay.fExpenseCategory')}</Label>
                <NativeSelect
                  id="pv-expense-category"
                  value={expenseCategory}
                  onChange={(event) => setExpenseCategory(event.target.value)}
                  disabled={!editable}
                >
                  <option value="">—</option>
                  {EXPENSE_CATEGORIES.map((candidate) => (
                    <option key={candidate} value={t(`pay.cat.${candidate}`)}>
                      {t(`pay.cat.${candidate}`)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div className="grid gap-1.5 sm:col-span-2 lg:col-span-3">
                <Label htmlFor="pv-expense-description">{t('pay.fExpenseDescription')}</Label>
                <Input
                  id="pv-expense-description"
                  maxLength={200}
                  value={expenseDescription}
                  onChange={(event) => setExpenseDescription(event.target.value)}
                  disabled={!editable}
                />
              </div>
            </div>
          ) : null}

        </CardContent>
      </Card>

      {/* ── S6: the tenders — how the money went out (PV-01) ─────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tenders.title')}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <TendersGrid
            kind="payment"
            rows={tenders}
            onChange={setTenders}
            editable={editable}
            voucherCurrency={currency}
            baseCurrency={base}
            currencies={currencyCodes}
            accounts={cashOptions}
            rateFor={rateFor}
            today={paymentDate}
            extraActions={
              paymentType === 'supplier' ? (
                <div className="min-w-64">
                  {/* PV-01: a cheque in hand, handed on. Read-only except the rate once picked. */}
                  <Typeahead
                    value={null}
                    onChange={onPickEndorsed}
                    options={chequePicker.options}
                    query={chequePicker.query}
                    onQueryChange={chequePicker.setQuery}
                    label={t('tenders.addEndorsed')}
                    placeholder={t('tenders.endorsedPh')}
                    emptyLabel={t('tenders.noInHand')}
                    loading={chequePicker.loading}
                    size="cell"
                  />
                </div>
              ) : null
            }
          />
        </CardContent>
      </Card>

      {/* ── The allocation grid — supplier payments only ───────────────── */}
      {paymentType === 'supplier' && partnerId ? (
        <Card>
          <CardHeader className="flex-row items-center">
            <CardTitle className="me-auto">{t('pay.allocTitle')}</CardTitle>
            {editable ? (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void onAutoAllocate()}
                  disabled={busy || billsLoading}
                >
                  <Wand2 aria-hidden /> {t('receipts.autoAllocate')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onClear} disabled={busy}>
                  <Eraser aria-hidden /> {t('receipts.clear')}
                </Button>
              </div>
            ) : null}
          </CardHeader>
          <CardContent className="grid gap-0">
            {rows.length === 0 && billRange.total === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {/* Until the list answers, "no open bills" is a claim about the
                    supplier's books we have not heard yet — and it is gated on
                    the TOTAL rather than on the rows, because a page past the
                    end of a real list holds no rows either and saying "no open
                    bills" there would be the paged version of the same lie. */}
                {billsLoading ? t('common.loading') : t('pay.noOpenBills')}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('pay.colBillNo')}</TableHead>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead className="text-end">{t('pay.colBillTotal')}</TableHead>
                    <TableHead className="text-end">{t('receipts.colAlreadyPaid')}</TableHead>
                    <TableHead className="text-end">{t('receipts.colBalanceDue')}</TableHead>
                    <TableHead className="w-36 text-end">{t('receipts.colApply')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const over = combinedOverRows.has(row.billId);
                    return (
                      <TableRow key={row.billId} className={cn(over && 'bg-red-50 dark:bg-red-950/40')}>
                        <TableCell>
                          <Link
                            to={`/purchases/bills/${row.billId}`}
                            className="font-mono text-primary hover:underline"
                          >
                            <bdi dir="ltr">{row.billCode}</bdi>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Ltr value={row.billDate} className="font-mono" />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={row.billTotal} currency={currency} />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={row.alreadyPaid} currency={currency} />
                        </TableCell>
                        <TableCell className="text-end">
                          <Amount value={row.balanceDue} currency={currency} />
                        </TableCell>
                        <TableCell>
                          <input
                            aria-label={t('pay.applyTo', { bill: row.billCode })}
                            dir="ltr"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={alloc[row.billId] ?? ''}
                            onChange={(event) =>
                              setAlloc((current) => ({ ...current, [row.billId]: event.target.value }))
                            }
                            disabled={!editable}
                            className={cn(
                              'h-9 w-full min-w-0 rounded-md border border-input bg-primary/5 px-2 text-end font-mono text-sm tabular-nums outline-none transition-colors',
                              'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
                              'disabled:cursor-not-allowed disabled:opacity-70',
                              over && 'border-destructive text-destructive',
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* NFR-42's visible half (§3 step 12), on the CASH grid only —
                the credit table below is the supplier's posted notes, a
                different and much shorter list with its own route. Drawn
                only when there is more than one page. Turning a page changes
                what is DRAWN and nothing else: the strip reads `alloc`, Save
                sends `alloc`, Auto-allocate asks the server. */}
            {billRange.pages > 1 ? (
              <div className="mt-2">
                <Pager
                  page={billPaging.page}
                  pages={billRange.pages}
                  first={billRange.first}
                  last={billRange.last}
                  total={billRange.total}
                  onPage={billPaging.setPage}
                  // The bare title — the convention every other `<Pager>` in
                  // the product follows. See the receipt editor's twin.
                  label={t('pay.allocTitle')}
                />
              </div>
            ) : null}

            {/* ── Supplier credit (PUR-04 §5 field 8) ─────────────────────
                The note-funded rows: a posted debit note's standing credit
                set against a bill. Same grid conventions as above — the
                derived facts read-only, one editable cell per row — and
                deliberately a SECOND table: these rows spend the note, not
                the voucher's cash, so they must not sit inside the cash
                budget's own grid. Capped per note by the server at
                grand total − already applied (the e2e-proven ceiling). */}
            {creditRows.length > 0 ? (
              <div className="mt-4 grid gap-2">
                <h3 className="text-sm font-bold">{t('pay.creditTitle')}</h3>
                <p className="text-xs text-muted-foreground">{t('pay.creditHint')}</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('pay.colNoteNo')}</TableHead>
                      <TableHead>{t('common.date')}</TableHead>
                      <TableHead className="text-end">{t('pay.colCreditTotal')}</TableHead>
                      <TableHead>{t('pay.colCreditBill')}</TableHead>
                      <TableHead className="w-36 text-end">{t('pay.colCreditApply')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {creditRows.map((note) => {
                      const credit = creditAlloc[note.noteId];
                      const overNote =
                        credit !== undefined &&
                        note.creditTotal !== null &&
                        MONEY_RE.test(credit.amount.trim()) &&
                        Money.of(credit.amount.trim(), currency).greaterThan(
                          Money.of(note.creditTotal, currency),
                        );
                      return (
                        <TableRow
                          key={note.noteId}
                          className={cn(overNote && 'bg-red-50 dark:bg-red-950/40')}
                        >
                          <TableCell>
                            <Link
                              to={`/purchases/returns/${note.noteId}`}
                              className="font-mono text-primary hover:underline"
                            >
                              <bdi dir="ltr">{note.noteCode}</bdi>
                            </Link>
                          </TableCell>
                          <TableCell>
                            {note.noteDate !== null ? (
                              <Ltr value={note.noteDate} className="font-mono" />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="text-end">
                            {note.creditTotal !== null ? (
                              <Amount value={note.creditTotal} currency={currency} />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {/* GEN-50, AND §3 STEP 8 TOGETHER — the two
                                halves this field needs, which no `<select>`
                                behind a paged read can hold at once.

                                The OFFER is the server's: `options` is one
                                panel of matches for what the reader typed,
                                over the supplier's whole open set, so a bill
                                on page three is findable by typing its
                                number rather than by paging to it.

                                The HELD value is not: `selected` resolves
                                through `seenBills`, which is every bill this
                                screen has been told about — the voucher's own
                                allocation rows, the carried bill, every page
                                drawn, every match shown. That is the prop the
                                component's header calls "the one that makes
                                the conversion safe", and without it clearing
                                the search box would blank a saved choice —
                                the same defect the old `<select>` had,
                                arrived at from the other side. */}
                            <Typeahead
                              label={t('pay.creditBillFor', { note: note.noteCode })}
                              value={credit?.billId || null}
                              selected={billPicker.optionFor(credit?.billId)}
                              options={billPicker.options}
                              query={billPicker.query}
                              onQueryChange={billPicker.setQuery}
                              loading={billPicker.loading}
                              emptyLabel={t('picker.noMatchingBill')}
                              // The old select's blank row, kept as the same
                              // character: choosing no bill is a legal answer
                              // (the note simply funds nothing yet), and
                              // `allocationBody` already drops a credit row
                              // with no `billId`.
                              clearLabel="—"
                              onChange={(billId) =>
                                setCreditAlloc((current) => ({
                                  ...current,
                                  [note.noteId]: {
                                    billId: billId ?? '',
                                    amount: current[note.noteId]?.amount ?? '',
                                  },
                                }))
                              }
                              disabled={!editable}
                              size="cell"
                              className="min-w-40"
                            />
                          </TableCell>
                          <TableCell>
                            <input
                              aria-label={t('pay.creditApplyTo', { note: note.noteCode })}
                              dir="ltr"
                              inputMode="decimal"
                              placeholder="0.00"
                              value={credit?.amount ?? ''}
                              onChange={(event) =>
                                setCreditAlloc((current) => ({
                                  ...current,
                                  [note.noteId]: {
                                    billId: current[note.noteId]?.billId ?? '',
                                    amount: event.target.value,
                                  },
                                }))
                              }
                              disabled={!editable}
                              className={cn(
                                'h-9 w-full min-w-0 rounded-md border border-input bg-primary/5 px-2 text-end font-mono text-sm tabular-nums outline-none transition-colors',
                                'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
                                'disabled:cursor-not-allowed disabled:opacity-70',
                                overNote && 'border-destructive text-destructive',
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : null}

            <div
              role="status"
              className={cn(
                'mt-2 flex flex-wrap items-center gap-2 rounded-b-md border-t-2 px-4 py-3 text-sm',
                strip.className,
              )}
            >
              {strip.icon}
              <span className="font-medium">{strip.text}</span>
              <span className="ms-auto flex gap-6 font-mono text-xs">
                <span>
                  {t('receipts.totalApplied')} <Amount value={view.totalApplied} />
                </span>
                <span>
                  {t('pay.amountPaidShort')}{' '}
                  <Amount value={MONEY_RE.test(amount.trim()) ? amount.trim() : '0.00'} />
                </span>
              </span>
            </div>

            {/* BR-FIN-04-08 — the excess is supplier credit, named, never a
                block. Not claimed before the grid is real: see stripState. */}
            {stripState === 'credit' ? (
              <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('pay.creditBanner')} <Amount value={view.unallocated} currency={currency} />
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* ── The approval panel (§5a) — what makes this screen different ──
          AND WHAT DID NOT MOVE WHEN THE TOOLBAR BECAME A DECLARATION. The
          roster entry that sent that batch here asked whether the trio below
          belongs in `DocumentToolbar`. It does not: it is a segmented control
          reporting a SERVER value, not a list of acts; PART 4.6's table has no
          row any of the three could name; it is not in the toolbar row at all;
          and the only shape that would carry it is the `node` escape, pinned
          at one. The argument in full is in `components/document-toolbar.tsx`
          under "WHAT IS NOT A TOOLBAR ACT". */}
      <Card>
        <CardHeader className="flex-row items-center gap-3">
          <CardTitle className="me-auto">{t('pay.approvalTitle')}</CardTitle>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={effectiveRequire}
              onCheckedChange={(checked) => {
                if (!editable || forcedApproval) return;
                setRequireApproval(checked === true);
              }}
              disabled={!editable || forcedApproval}
              aria-label={t('pay.requireApproval')}
            />
            {t('pay.requireApproval')}
            {forcedApproval ? (
              <span className="text-xs text-muted-foreground">{t('pay.requireForced')}</span>
            ) : null}
          </label>
        </CardHeader>
        {effectiveRequire ? (
          <CardContent className="grid gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {(
                [
                  ['pending', <Hourglass key="i" className="size-4" aria-hidden />],
                  ['approved', <CheckCircle2 key="i" className="size-4" aria-hidden />],
                  ['rejected', <XCircle key="i" className="size-4" aria-hidden />],
                ] as const
              ).map(([candidate, icon]) => (
                <Gated
                  key={candidate}
                  permission="accounting.approve"
                  reasonKey="perm.cannot.decidePayments"
                >
                <button
                  type="button"
                  aria-pressed={approvalStatus === candidate}
                  // GEN-208, spec line 4707: "Nothing must happen until the
                  // action button is clicked." For the two acts GEN-209 names,
                  // this click now OPENS THE QUESTION and does nothing else —
                  // no mutation, no request. `pending` is unchanged and still
                  // writes on click; the narrowing is argued at `approvalAsk`.
                  onClick={() =>
                    candidate === 'pending'
                      ? void onSetApproval(candidate)
                      : setApprovalAsk(candidate)
                  }
                  disabled={locked || isNew || busy}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                    approvalStatus === candidate
                      ? candidate === 'approved'
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                        : candidate === 'rejected'
                          ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
                          : 'border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                      : 'text-muted-foreground hover:text-foreground',
                    (locked || isNew) && 'cursor-not-allowed opacity-60',
                  )}
                >
                  {icon}
                  {t(`pay.approval.${candidate}`)}
                </button>
                </Gated>
              ))}
              {/* BR-FIN-04-04: the date chip only while Approved.
                  ERR-FIN-04-03 — "looked for the approval date on a pending
                  payment: THE CHIP IS NOT DRAWN" — is this same condition read
                  from the user's side, and `approvalStatus === 'approved'` is
                  the half that carries it.

                  THE TWO CONJUNCTS ARE REDUNDANT TODAY, and the reason is a
                  server invariant rather than anything visible here: every
                  path back to pending or rejected nulls the date alongside the
                  status (`payments.service.ts` does it in all three — the
                  amount/allocation edit that unapproves, the explicit decision
                  write, and the internal reset). So `approvalDate` is never
                  non-null while pending, and either conjunct alone would draw
                  correctly against the API as it stands.

                  Kept as a pair deliberately: the redundancy is defence in
                  depth against those three writes drifting apart, and the
                  STATUS test is the one that must survive — it is true of the
                  rule ("only while approved") rather than true of a current
                  invariant that a fourth write path could quietly break. */}
              {approvalStatus === 'approved' && payment?.approvalDate ? (
                <span className="ms-auto flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                  {payment.approvedByEmail ? `${t('pay.approvedBy')} ${payment.approvedByEmail} · ` : ''}
                  <Ltr value={payment.approvalDate} />
                </span>
              ) : null}
            </div>
            {/* ADM-02 §8's shared box, two outcomes, for GEN-209's fifth act.
                Rendered inside the `effectiveRequire` branch and behind the
                same `!locked && !isNew` test the buttons carry, so the dialog
                cannot outlive the state that allows the decision — the
                convention the delete and the cancel above set. BR-FIN-04-07's
                "a locked voucher must make the approval switch and the three
                approval buttons inert" is the rule that clause serves: a
                question left standing over a voucher that locked underneath it
                would be an inert button wearing a live dialog.

                GEN-206, spec line 4703: "The action button must never read OK,
                Yes, or Confirm on its own." `pay.approval.approved` and
                `pay.approval.rejected` were NOT reusable as the verb — they
                are the STATUS adjectives painted on the toggle ("Approved",
                "Rejected"), and a dialog whose action button reads "Approved"
                describes a state rather than naming an act. Hence the two new
                verb keys. */}
            {approvalAsk !== null && !locked && !isNew ? (
              <ConfirmBox
                open
                title={t(
                  approvalAsk === 'approved'
                    ? 'pay.confirmApproveTitle'
                    : 'pay.confirmRejectTitle',
                )}
                body={t(
                  approvalAsk === 'approved' ? 'pay.confirmApproveBody' : 'pay.confirmRejectBody',
                )}
                confirmLabel={t(approvalAsk === 'approved' ? 'pay.approveVerb' : 'pay.rejectVerb')}
                busy={busy}
                onConfirm={() => {
                  const decision = approvalAsk;
                  setApprovalAsk(null);
                  void onSetApproval(decision);
                }}
                onCancel={() => setApprovalAsk(null)}
              />
            ) : null}
            {isNew ? (
              <p className="text-xs text-muted-foreground">{t('pay.approvalSaveFirst')}</p>
            ) : null}
          </CardContent>
        ) : null}
      </Card>

      {/* ── The posting panel + notes ──────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('receipts.notes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              aria-label={t('receipts.notes')}
              placeholder={t('pay.notesPh')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid gap-3">
            {/* §12's preview-in-words — the pattern every posting screen
                copies. BR-FIN-04-10: each variant states what posting will
                ACTUALLY write for this voucher's shape — see the flags'
                comment beside `previewNoteOnly`. */}
            <p className="text-xs text-muted-foreground">
              {previewNoteOnly
                ? t('pay.postingPreviewNoteOnly')
                : paymentType === 'supplier'
                  ? t('pay.postingPreview', { account: accountCode ?? '…' })
                  : t('pay.postingPreviewExpense', {
                      account: accountCode ?? '…',
                      debit: debitOption ? `${debitOption.code} — ${debitOption.name}` : '…',
                    })}
            </p>
            {previewFxLine ? (
              <p className="text-xs text-muted-foreground">{t('pay.postingPreviewFxLine')}</p>
            ) : null}
            {/* The settlement side of a foreign payable: this voucher freezes
                its own rate, the bill it pays froze a different one, and the
                gap is GEN-113's realised exchange gain or loss — which "must
                never change the invoice". What is converted here is the AMOUNT
                PAID. Hidden entirely for a base-currency voucher. */}
            <FxPanel doc={payment} />
            {!locked ? (
              <Gated
                permission="accounting.approve"
                reasonKey="perm.cannot.postPayments"
                className="w-full"
              >
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  // BR-FIN-04-02's approval LIMIT lives in `postable` and keeps
                  // the button live-and-disabled with the amber line beneath —
                  // 16.7 row 3, beside row 2's greying. Two different refusals,
                  // two different shapes.
                  disabled={busy || !postable}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('pay.postPayment')}
                </Button>
              </Gated>
            ) : null}
            {/* BR-FIN-04-02's exact amber line, drawn while the gate blocks. */}
            {!locked && !approvalSatisfied ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('pay.approvalLine')}
              </p>
            ) : null}
            {/* SPEC-VERSUS-BUILD DEVIATION, carrying NO rule identifier —
                FIN-04 has no approval rule and no approval row, like FIN-03
                and SAL-05. The banner is PART 16's pattern applied where the
                spec gave none, so nothing here is a rule being enforced.

                The wording read "ask an approver" until the ERR sweep. The
                clause is now "sent to an approver", the phrase the spec uses
                in all six places this refusal appears — the point being that
                one product should not answer the same question four different
                ways, and three of the four ways had no source at all. */}
            {!locked && !canApprove ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('pay.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
